/**
 * Turns an API error into a translation key.
 *
 * The API never speaks the language the person picked on the site. Its locale
 * comes from `getLanguageUser()` — the country behind the request IP — and its
 * `lang/` folder has no `pt` at all (it is `br`), so the `Accept-Language`
 * header src/configs/axios.ts sends is ignored for response copy. A raw
 * `response.data.message` therefore reaches the screen in English (or in the
 * IP country's language) no matter what the locale switcher says.
 *
 * So no alert renders API text. The API messages are stable English literals in
 * app/Http/Controllers/UserController.php and app/Services/UserService.php, and
 * each one maps to a key we own and translate in all ten locales. Anything not
 * in the table falls back to the generic key the call site passes.
 */

/** Normalized error every auth callback receives: HTTP status plus the API's raw message. */
export interface ApiErrorInfo {
  status?: number | undefined;
  message?: string | undefined;
}

interface ApiErrorKeyOptions {
  /** Used when neither the message nor the status resolves to a key. */
  fallbackKey: string;
  /**
   * Per-status keys for the endpoints where the status alone carries the meaning
   * — e.g. 401 on /user/login is always wrong email/password. Consulted only
   * after the message table misses.
   */
  statusKeys?: Record<number, string>;
}

// Compared after normalize(): lowercased, punctuation dropped, whitespace collapsed.
// Left column is the API literal, right column the key we translate. Every entry below was
// captured from the API running locally, not read off the source — the response for a case
// is often a FormRequest's validation line rather than the controller's own message.
const MESSAGE_KEYS: Record<string, string> = {
  // CaptchaVerification / CloudFlareTurnstile / GoogleReCaptcha middleware, and the exceptions
  // CaptchaService throws before them
  // normalize() drops the apostrophe from the API's "Could't" typo, so the key is spelled the way
  // it arrives after normalization
  'couldt verify the turnstile token': 'common:login.error_captcha_failed',
  'couldt verify the recaptcha token': 'common:login.error_captcha_failed',
  'turnstile validation is required': 'common:login.error_captcha_failed',
  'turnstile validation failed': 'common:login.error_captcha_failed',
  // UserController::login
  'invalid credentials': 'common:login.error_invalid_credentials',
  'unable to process login please try again later': 'common:login.error_login_unavailable',
  // LoginUserRequest's exists:usuario rule — what an unknown email actually returns
  'the selected login is invalid': 'common:login.mensage_error_user_does_not_exist',
  // UserController::registrationErrorResponse + CreateUserRequest validation
  'the email has already been taken': 'common:login.error_email_already_taken',
  'unable to create your account please try again later': 'common:login.mensage_error_create_user_generic',
  // UserService::create
  'the email domain is invalid please check your email address': 'common:login.error_email_domain_invalid',
  'user email domain is blacklisted': 'common:login.error_email_domain_blocked',
  'user not found': 'common:login.mensage_error_user_does_not_exist',
  // UserService trial eligibility
  'user already activated 3 day trial': 'common:login.error_trial_already_used',
  'this trial is not available in your region': 'common:login.error_trial_region',
  // UserController::respondToSsoLogin
  'invalid or expired sso token': 'common:login.google_error_token_invalid',
  'something went wrong during sso login': 'common:login.google_error_generic',
  // AbstractSocialiteSsoProviderAdapter — the messages the SSO adapters actually throw. Only the
  // InvalidSsoTokenException DEFAULT ('Invalid or expired SSO token') was mapped, and no adapter throws
  // it, so a real SSO failure always fell through to the call site's fallback key. The authorization-code
  // one was captured from staging (`401 {"message":"Invalid or expired authorization code"}`); the other
  // two are the sibling throws in userFromToken/userFromCode.
  'invalid or expired authorization code': 'common:login.sso_invalid_session',
  'invalid oauth state': 'common:login.sso_invalid_session',
  'invalid or expired access token': 'common:login.google_error_token_invalid',
  // CheckoutController::checkout / ::trial and CheckoutService
  'user not authenticated': 'common:login.message_error_needs_tobe_logged',
  'needs to be logged in to checkout': 'common:login.message_error_needs_tobe_logged',
  'gateway id and gateway external id are required': 'components/checkout:error_message.generic.internal',
  'internal server error': 'components/checkout:error_message.generic.internal',
  'invalid gateway': 'components/checkout:error_message.generic.internal',
  'invalid plan': 'components/checkout:error_message.generic.internal',
  'invalid trial plan': 'components/checkout:error_message.generic.internal',
  'plan has no type': 'components/checkout:error_message.generic.internal',
  'invalid credit card': 'components/checkout:error_message.invalid.card',
  'failed to checkout payment please try again or contact support': 'components/checkout:error_message.generic.checkout_failed',
  // CheckoutService::resolvePromoCodeForCheckout speaks its own vocabulary: it prefixes every
  // message with `promo_code_`, including the ones /promo-codes/validate answers with bare. Those
  // situations already have copy in all ten locales — the very text the coupon field shows — so
  // they point at it instead of a second wording for the same thing.
  'promo code not supported on gateway': 'pages/pricing:coupon.errors.gateway_not_eligible',
  'promo code gateway not eligible': 'pages/pricing:coupon.errors.gateway_not_eligible',
  'promo code gateway unsynced': 'pages/pricing:coupon.errors.gateway_sync_pending',
  'promo code gateway sync pending': 'pages/pricing:coupon.errors.gateway_sync_pending',
  'promo code recurring unsupported gateway': 'pages/pricing:coupon.errors.recurring_unsupported_gateway',
  'promo code not found': 'pages/pricing:coupon.errors.not_found',
  'promo code disabled': 'pages/pricing:coupon.errors.disabled',
  'promo code expired': 'pages/pricing:coupon.errors.expired',
  'promo code exhausted': 'pages/pricing:coupon.errors.exhausted',
  'promo code country not eligible': 'pages/pricing:coupon.errors.country_not_eligible',
  'promo code plan not eligible': 'pages/pricing:coupon.errors.plan_not_eligible',
  'promo code per customer limit': 'pages/pricing:coupon.errors.per_customer_limit',
};

// A declined card is the one API message with the provider's own text appended
// ("Failed to checkout payment! {reason}"), so it cannot be matched whole. The
// reason itself is the acquirer's English string and never reaches the screen —
// it is already in the API log, which is where a support ticket reads it.
const MESSAGE_PREFIX_KEYS: Array<[string, string]> = [['failed to checkout payment', 'components/checkout:error_message.generic.card_declined']];

/**
 * Trial registration is the one endpoint whose status carries meaning the message table cannot:
 * a 409 is UserService's eligibility check, and its message is assembled from the reason it
 * computed, so there is no literal to match. 401 is deliberately absent — the API uses it for
 * both "already used" and "not in your region", and both of those messages are in the table.
 */
export const TRIAL_STATUS_KEYS: Record<number, string> = {
  409: 'common:login.error_trial_not_eligible',
};

function normalize(message: string): string {
  return message
    .toLowerCase()
    .replace(/[.!?,;:'"]/g, '')
    .replace(/[-_/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function apiErrorMessageKey(error: ApiErrorInfo | undefined, { fallbackKey, statusKeys }: ApiErrorKeyOptions): string {
  const normalized = error?.message ? normalize(error.message) : undefined;

  const mappedByMessage = normalized ? MESSAGE_KEYS[normalized] : undefined;
  if (mappedByMessage) return mappedByMessage;

  const mappedByPrefix = normalized ? MESSAGE_PREFIX_KEYS.find(([prefix]) => normalized.startsWith(prefix))?.[1] : undefined;
  if (mappedByPrefix) return mappedByPrefix;

  const mappedByStatus = error?.status !== undefined ? statusKeys?.[error.status] : undefined;
  if (mappedByStatus) return mappedByStatus;

  return fallbackKey;
}

/**
 * Collapses the two shapes an axios rejection can arrive in — a response with a
 * JSON body, or a network/parse failure with no response at all — into
 * ApiErrorInfo. Callers get the same object either way, so no call site has to
 * reach into `err.response.data` again.
 */
export function toApiErrorInfo(error: unknown): ApiErrorInfo {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } } | undefined)?.response;
  const message = response?.data?.message;

  return {
    status: response?.status,
    message: typeof message === 'string' ? message : undefined,
  };
}
